import type { V3 } from "backoffice-api"
import type { ChatMessage } from "./AIChat"

type AiAssistantEvent = V3["getAiAssistantEvents"]["data"][number]

// Payloads are unchecked at the schema (see `AiAssistantEvent`), so each field
// is narrowed where it is read.
const payloadString = (payload: unknown, key: string) => {
  if (
    typeof payload !== "object" ||
    payload === null ||
    Array.isArray(payload)
  ) {
    return undefined
  }
  const value = (payload as Record<string, unknown>)[key]
  return typeof value === "string" ? value : undefined
}

const payloadStringArray = (payload: unknown, key: string) => {
  if (
    typeof payload !== "object" ||
    payload === null ||
    Array.isArray(payload)
  ) {
    return []
  }
  const value = (payload as Record<string, unknown>)[key]
  if (!Array.isArray(value)) return []

  return [...new Set(value.filter((id) => typeof id === "string"))]
}

export const answerPlaceholder = (): ChatMessage => ({
  messageType: "Response",
  text: "",
})

export const errorMessage = (text: string): ChatMessage => ({
  messageType: "Error",
  text,
})

export const replaceAnswer = (messages: ChatMessage[], answer: ChatMessage) => {
  return [...messages.slice(0, -1), answer]
}

// `token_batch` is a provisional preview and is appended; `done` carries the
// committed answer and replaces its text, since a recovered turn can show a partial
// preview and then settle with the full answer.
export const applyEvents = (
  messages: ChatMessage[],
  events: AiAssistantEvent[],
  describeError: (type: unknown) => string
) => {
  return events.reduce((current, event) => {
    // Never write over the learner's own question.
    const answer = current.at(-1)
    if (!answer || answer.messageType === "Question") return current

    switch (event.kind) {
      case "token_batch": {
        const text = payloadString(event.payload, "text") ?? ""
        return replaceAnswer(current, { ...answer, text: answer.text + text })
      }
      case "product_suggestion": {
        // Arrives just before `done`, which must keep it.
        const productIds = payloadStringArray(event.payload, "product_ids")
        if (productIds.length === 0) return current
        return replaceAnswer(current, { ...answer, productIds })
      }
      case "done": {
        // `done` ends polling, so an answer it does not carry would otherwise
        // leave an empty response on screen.
        const content = payloadString(event.payload, "content")
        if (content === undefined) {
          return replaceAnswer(current, errorMessage(describeError(null)))
        }
        return replaceAnswer(current, {
          ...answer,
          messageType: "Response",
          text: content,
        })
      }
      case "error":
        return replaceAnswer(
          current,
          errorMessage(describeError(payloadString(event.payload, "type")))
        )
      default:
        return current
    }
  }, messages)
}
